import type { User } from "@supabase/supabase-js";
import type { AppSupabaseClient } from "./supabase";
import type { LocalStore, StoredGame } from "@onchainreps/storage";

export interface SyncResult {
  uploaded: number;
  skipped: number;
  error: string | null;
}

function handleFromUser(user: User): string {
  const source =
    (user.user_metadata?.handle as string | undefined) ??
    (user.email ? user.email.split("@")[0] : undefined) ??
    "player";
  const cleaned = source
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, "")
    .slice(0, 24);
  return cleaned.length > 0 ? cleaned : "player";
}

export async function ensureProfile(client: AppSupabaseClient, user: User): Promise<string | null> {
  const { data, error } = await client
    .from("profiles")
    .select("id, handle")
    .eq("id", user.id)
    .maybeSingle();
  if (error) return error.message;
  if (data) return data.handle as string;

  const handle = handleFromUser(user);
  const insert = await client
    .from("profiles")
    .insert({
      id: user.id,
      handle,
      display_name: (user.user_metadata?.display_name as string | undefined) ?? user.email ?? handle,
    })
    .select("handle")
    .maybeSingle();
  if (insert.error) {
    if (insert.error.code === "23505") {
      const fallback = await client
        .from("profiles")
        .insert({
          id: user.id,
          handle: `${handle}_${user.id.slice(0, 6)}`,
          display_name: user.email ?? handle,
        })
        .select("handle")
        .maybeSingle();
      return fallback.error ? fallback.error.message : (fallback.data?.handle as string);
    }
    return insert.error.message;
  }
  return (insert.data?.handle as string) ?? handle;
}

function toRow(game: StoredGame, userId: string) {
  return {
    id: game.id,
    user_id: userId,
    source: game.source,
    external_game_id: game.id,
    pgn: game.pgn,
    result: game.result,
    white: game.white,
    black: game.black,
    opening: game.opening,
    eco: game.eco,
    played_at: game.playedAt,
    imported_at: game.importedAt,
    accuracy_white: game.accuracyWhite,
    accuracy_black: game.accuracyBlack,
    blunders_white: game.blundersWhite,
    blunders_black: game.blundersBlack,
    player_color: game.playerColor,
  };
}

export async function syncGames(
  client: AppSupabaseClient,
  user: User,
  store: LocalStore | null,
): Promise<SyncResult> {
  if (!store) return { uploaded: 0, skipped: 0, error: null };
  const games = await store.listGames();
  if (games.length === 0) return { uploaded: 0, skipped: 0, error: null };

  const rows = games.map((game) => toRow(game, user.id));
  const { error } = await client
    .from("games")
    .upsert(rows, { onConflict: "user_id,source,external_game_id" });
  if (error) return { uploaded: 0, skipped: games.length, error: error.message };
  return { uploaded: rows.length, skipped: 0, error: null };
}

export async function syncDrills(
  client: AppSupabaseClient,
  user: User,
  store: LocalStore | null,
): Promise<SyncResult> {
  if (!store) return { uploaded: 0, skipped: 0, error: null };
  const drills = await store.listDrills();
  if (drills.length === 0) return { uploaded: 0, skipped: 0, error: null };

  const rows = drills.map((drill) => ({
    id: drill.id,
    user_id: user.id,
    game_id: drill.gameId,
    ply: drill.ply,
    fen: drill.fen,
    best_move: drill.bestMove,
    category: drill.category,
    loss: drill.loss,
    side: drill.side,
    created_at: drill.createdAt,
  }));

  const { error } = await client.from("drills").upsert(rows, { onConflict: "id" });
  if (error) return { uploaded: 0, skipped: drills.length, error: error.message };
  return { uploaded: rows.length, skipped: 0, error: null };
}
